/**
 * Decks: the questions a deck template asks, and the parts list they make.
 *
 * Jonathon, 2026-10-11: "there needs to be a pressure treated template and a
 * trex template ... use a 10% waste. do 16" spacing of joists on pressure
 * treated. 12" on center for trex. also do enough blocking for picture frame
 * on decks." Nothing on a deck is measured off a report the way a roof is: the
 * rep types the size, and the framing follows from it by rule. So the rules are
 * here, one function per part, and each template line names the count it takes
 * (`count_source`), the way a siding line takes the rep's vent count.
 *
 * The rules are Brandon Roberts's, the deck subcontractor, from three years of
 * texts with Jonathon — see docs/DECK-REFERENCE.md for each one and its date:
 * posts about 8 ft apart, 2½ bags of concrete a post, nothing under 2×10, a
 * 2×12 ledger and sides and a doubled 2×12 across the front, the joists hung at
 * both ends, pressure blocking past 8 ft, 3 LedgerLOKs a post.
 *
 * The counting rules themselves are in deckCounts.ts. Nothing in either file
 * touches money: they decide how many, the price list decides what they cost
 * and the ladder does the arithmetic.
 */

const num = (value: unknown): number => {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
};

/* -------------------------------------------------------------------------
 * Which templates are decks
 * ---------------------------------------------------------------------- */

/** The roof type a pressure-treated deck template carries. Never renamed. */
export const DECK_ROOF_TYPE = "Deck";
/** And a Trex one. Never renamed. */
export const TREX_DECK_ROOF_TYPE = "Trex Deck";

export function isDeckType(roofType: string | null | undefined): boolean {
  const type = (roofType ?? "").trim().toLowerCase();
  return (
    type === DECK_ROOF_TYPE.toLowerCase() ||
    type === TREX_DECK_ROOF_TYPE.toLowerCase()
  );
}

export function isTrexDeckType(roofType: string | null | undefined): boolean {
  return (
    (roofType ?? "").trim().toLowerCase() === TREX_DECK_ROOF_TYPE.toLowerCase()
  );
}

/**
 * Joist spacing, in inches. Jonathon, 2026-10-11: 16" on pressure treated,
 * 12" on Trex — composite boards sag between joists that wood would span.
 */
export function joistSpacingFor(roofType: string | null | undefined): number {
  return isTrexDeckType(roofType) ? 12 : 16;
}

/** Jonathon, 2026-10-11: "use a 10% waste." */
export const DECK_WASTE_PCT = 10;

/* -------------------------------------------------------------------------
 * The questions
 * ---------------------------------------------------------------------- */

/** Every answer the deck form collects before it generates. */
export interface DeckAnswers {
  /** Along the house, in feet. */
  widthFt: number;
  /** Out from the house, in feet. */
  depthFt: number;
  /** The deck's surface above the ground, in feet. */
  heightFt: number;
  /** Fixed to the house on a ledger, or standing on its own posts. */
  attached: boolean;
  /**
   * A second-story deck, ticked by the rep. Jonathon, 2026-10-11: "raised
   * means 2nd story", and the crew is paid more for one.
   */
  secondStory: boolean;
  /**
   * An old deck comes down first. Jonathon, 2026-10-11, asked whether every
   * deck is a replacement: "Ask 'replacing a deck?'". The tear-down is in the
   * scope only when it is ticked.
   */
  replacing: boolean;
  /** How many sets of steps. */
  stairSets: number;
  /** How wide each set is, in feet. */
  stairWidthFt: number;
  /**
   * The deck has railing round it. Jonathon, 2026-10-12, asked whether a deck
   * should start with railing on every open side or none: "just ask if
   * railing is required." Unticked, the deck has none, and neither do its
   * steps (Jonathon, 2026-10-13: "no railing means no handrails either").
   */
  railingRequired: boolean;
  /**
   * Feet of railing around the deck, not counting the stairs, whose rails
   * are worked out from their own length. Null until the rep types one: the
   * open sides less the stair openings stand in.
   */
  railingLf: number | null;
}

export function defaultDeckAnswers(): DeckAnswers {
  return {
    widthFt: 0,
    depthFt: 0,
    heightFt: 3,
    attached: true,
    secondStory: false,
    replacing: false,
    stairSets: 1,
    stairWidthFt: 4,
    railingRequired: false,
    railingLf: null,
  };
}

/** Square feet of deck. The crew is paid on this and the customer reads it. */
export function deckSquareFeet(answers: DeckAnswers): number {
  return Math.max(0, num(answers.widthFt)) * Math.max(0, num(answers.depthFt));
}

/** At 30 inches a deck needs a guard rail on every open side (code). */
export const GUARD_RAIL_FROM_FT = 2.5;

export function needsGuardRail(answers: DeckAnswers): boolean {
  return num(answers.heightFt) >= GUARD_RAIL_FROM_FT;
}

/** The sides a railing runs along: the house side has none on an attached deck. */
export function openPerimeterLf(answers: DeckAnswers): number {
  const w = Math.max(0, num(answers.widthFt));
  const d = Math.max(0, num(answers.depthFt));
  return answers.attached ? w + 2 * d : 2 * w + 2 * d;
}

/** The railing the form starts with: every open side, less the stair openings. */
export function defaultRailingLf(answers: DeckAnswers): number {
  const openings =
    Math.max(0, Math.round(num(answers.stairSets))) *
    Math.max(0, num(answers.stairWidthFt));
  return Math.max(0, openPerimeterLf(answers) - openings);
}

/** The deck's own railing: none unless the rep says it is required. */
export function railingLfOf(answers: DeckAnswers): number {
  if (!answers.railingRequired) return 0;
  return answers.railingLf === null
    ? defaultRailingLf(answers)
    : Math.max(0, num(answers.railingLf));
}

/** The tokens the answers satisfy — see `lineApplies` in flatRoof.ts. */
export function deckTokens(answers: DeckAnswers): ReadonlySet<string> {
  const tokens = new Set<string>();
  tokens.add(answers.secondStory ? "deck:second_story" : "deck:one_story");
  tokens.add(answers.attached ? "deck:attached" : "deck:freestanding");
  tokens.add(answers.replacing ? "deck:replacing" : "deck:new_build");
  return tokens;
}

/** The `applies_when` prefix a deck template uses. */
export const DECK_TOKEN_PREFIXES: readonly string[] = ["deck:"];

/* -------------------------------------------------------------------------
 * The steps
 * ---------------------------------------------------------------------- */

/** A riser no taller than 7½ in, a tread of two boards (11 in, 10½ run). */
const MAX_RISE_IN = 7.5;
const TREAD_RUN_IN = 10.5;
/** A stringer every 16 inches across the stair. */
const STRINGER_SPACING_IN = 16;

const up = (n: number): number => Math.ceil(n - 1e-9);

/** One set of steps, worked out from the deck's height. */
export interface StairSet {
  risers: number;
  treads: number;
  stringers: number;
  /** Feet, along the slope, with a foot to cut the ends. */
  stringerFt: number;
}

export function stairSet(answers: DeckAnswers): StairSet | null {
  const riseIn = Math.max(0, num(answers.heightFt)) * 12;
  if (riseIn <= 0) return null;
  const risers = up(riseIn / MAX_RISE_IN);
  const treads = Math.max(0, risers - 1);
  const runIn = treads * TREAD_RUN_IN;
  const slopeFt = Math.sqrt(riseIn * riseIn + runIn * runIn) / 12;
  const widthIn = Math.max(0, num(answers.stairWidthFt)) * 12;
  return {
    risers,
    treads,
    stringers: Math.floor(widthIn / STRINGER_SPACING_IN) + 1,
    stringerFt: Math.round((slopeFt + 1) * 100) / 100,
  };
}

/* -------------------------------------------------------------------------
 * The counts a template line can take
 * ---------------------------------------------------------------------- */

/**
 * Each key is a `count_source` a deck template line can carry. Never renamed:
 * they are stored on template lines, and the database's CHECK lists them.
 *
 * Lumber comes in lengths, so a length is its own count: a 10 ft deep deck
 * buys 10 ft joists and none of the others, and a line whose count is zero is
 * dropped.
 */
export const DECK_LENGTHS = [8, 10, 12, 16] as const;
export type DeckLength = (typeof DECK_LENGTHS)[number];

export const DECK_COUNT_SOURCES = [
  "deck_sqft",
  "deck_extra_stairs",
  "deck_post_8",
  "deck_post_10",
  "deck_post_12",
  "deck_post_16",
  "deck_footing_tube",
  "deck_concrete_bag",
  "deck_post_base",
  "deck_wedge_anchor",
  "deck_2x12_8",
  "deck_2x12_10",
  "deck_2x12_12",
  "deck_2x12_16",
  "deck_2x10_8",
  "deck_2x10_10",
  "deck_2x10_12",
  "deck_2x10_16",
  "deck_joist_hanger",
  "deck_hanger_screw_short_box",
  "deck_hanger_screw_long_box",
  "deck_ledgerlok_box",
  "deck_tension_tie_kit",
  "deck_ledger_flashing",
  "deck_board_16",
  "deck_screw_box",
  "deck_nail_box",
  "deck_rail_post",
  "deck_carriage_bolt_pack",
  "deck_washer_pack",
  "deck_nut_pack",
  "deck_rail_2x4_16",
  "deck_rail_cap_2x6_16",
  "deck_baluster",
  "deck_stair_support_2x6_8",
] as const;
export type DeckCountSource = (typeof DECK_COUNT_SOURCES)[number];

/** One count and how it was worked out, for the order's calc note. */
export interface DeckCount {
  qty: number;
  note: string;
}

export type DeckCounts = Partial<Record<DeckCountSource, DeckCount>>;

/* -------------------------------------------------------------------------
 * Onto the template's lines
 * ---------------------------------------------------------------------- */

/** A template line, as far as the deck counts are concerned. */
export interface DeckCountedLine {
  qty?: unknown;
  count_source?: string | null;
  count_note?: string | null;
}

/**
 * The same lines with the deck's counts written in as quantities.
 *
 * A line naming a deck count takes it, and a count of nothing drops the line:
 * a 10 ft deep deck buys no 16 ft joists. Lines naming no deck count pass
 * through untouched.
 */
export function withDeckCounts<L extends DeckCountedLine>(
  items: readonly L[],
  counts: DeckCounts,
): L[] {
  const out: L[] = [];
  for (const item of items) {
    const source = item.count_source ?? null;
    if (
      !source ||
      !(DECK_COUNT_SOURCES as readonly string[]).includes(source)
    ) {
      out.push(item);
      continue;
    }
    const count = counts[source as DeckCountSource];
    if (!count || count.qty <= 0) continue;
    out.push({ ...item, qty: count.qty, count_note: count.note });
  }
  return out;
}
